import { useEffect, useRef, type RefObject } from 'react'

type AnyRef = RefObject<HTMLElement | null>

/**
 * Closes a popover / dropdown on outside pointer-down and Escape — the one
 * dismiss primitive shared by ToolbarPopover, the editor-toolbar burger, the
 * ModeToggle overflow list and the dev render menu (each of which used to
 * hand-roll its own subtly-different copy).
 *
 * @param refs   Element(s) that count as "inside" — a mousedown within any is
 *               ignored. Pass the menu AND its trigger so clicking the trigger
 *               doesn't self-close before its own onClick toggles.
 * @param onClose Called on an outside mousedown or Escape.
 * @param opts.enabled       Only wire listeners while true (usually the open flag).
 * @param opts.capture       Listen in capture phase — needed to beat canvas /
 *                           editor surfaces that stop bubbling. Default false.
 * @param opts.openGuard     Defer the mousedown listener by one rAF so the very
 *                           click that opened the menu doesn't immediately close
 *                           it. Default false.
 * @param opts.ignoreSelector A mousedown inside an element matching this selector
 *                           is treated as "inside" — e.g. '.ws-popover' so a
 *                           portaled child popover doesn't dismiss its parent.
 */
export function useDismiss(
  refs: AnyRef | AnyRef[],
  onClose: () => void,
  opts: {
    enabled?: boolean
    capture?: boolean
    openGuard?: boolean
    ignoreSelector?: string
  } = {},
) {
  const { enabled = true, capture = false, openGuard = false, ignoreSelector } = opts
  // Hold the latest refs/onClose so the effect only re-subscribes when the
  // behavioral options change, not on every render (callers pass inline arrays
  // and closures).
  const refsRef = useRef(refs)
  refsRef.current = refs
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    if (!enabled) return
    const isInside = (t: Node | null) => {
      if (!t) return false
      if (ignoreSelector && (t as Element)?.closest?.(ignoreSelector)) return true
      const list = Array.isArray(refsRef.current) ? refsRef.current : [refsRef.current]
      return list.some(r => r.current?.contains(t))
    }
    const onDown = (e: MouseEvent) => { if (!isInside(e.target as Node)) onCloseRef.current() }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCloseRef.current() }
    let raf = 0
    const attach = () => document.addEventListener('mousedown', onDown, capture)
    if (openGuard) raf = requestAnimationFrame(attach)
    else attach()
    document.addEventListener('keydown', onKey)
    return () => {
      if (raf) cancelAnimationFrame(raf)
      document.removeEventListener('mousedown', onDown, capture)
      document.removeEventListener('keydown', onKey)
    }
  }, [enabled, capture, openGuard, ignoreSelector])
}
